import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from '../../../shared/services/baseQuery';
import type { TimelineData } from '../TimeLineBlock';

import {
  type AuditColumnDefApiResponse,
  type AuditFilterDef,
  type AuditFiltersOptionsApiResponse,
  type AuditLogSearchApiResponse,
  type AuditLogSearchRequest,
  type AuditLogSearchResponse,
  AuditLogType,
  type CompleteReviewApiResponse,
  type CompleteReviewRequest,
  type CompleteReviewResponse,
  type EntitlementsSearchResponse,
  type FunctionRec,
  type GetAuditColumnsApiResponse,
  type InsightsApiResponse,
  type PostReviewApiResponse,
  type PostReviewRequest,
  type RequestMetadata,
  type RequestReviewDetailsApiResponse,
  type ReviewActivity,
  type ReviewDetailInfo,
  type ReviewState,
  type SearchEntitlementsRequest,
} from './auditLogTypes';
import mapUniqueFunctions, {
  parseAuditExportMaxDateRangeMonths,
} from './auditLogUtils';

const ACTIVITY_LOG_INTL_BASE_URL = '/api/activity-log/v1';

const transformRequestReviewDetailsApiResponse = (
  obj: RequestReviewDetailsApiResponse
) => {
  if (obj.status === 200) {
    const {
      state,
      readOnly,
      requestDetail,
      reviewDetail,
      isActivityLogEnabled,
      requestType: dataRequestType,
    } = obj.data;

    const exportMaxDateRangeMonths = parseAuditExportMaxDateRangeMonths(
      obj.data
    );

    // Handle cases ACCESS_DENIED & INGESTION_IN_PROGRESS)
    if (!requestDetail || !reviewDetail) {
      return {
        requestMetadata: null,
        isReadOnly: Boolean(readOnly),
        reviewSummary: null,
        state,
        launchDetail: [],
        isActivityLogEnabled,
        exportMaxDateRangeMonths,
      };
    }

    const ffRoleName =
      typeof requestDetail.ffRoleName === 'string'
        ? requestDetail.ffRoleName.trim()
        : '';
    const ffid =
      typeof requestDetail.ffid === 'string' ? requestDetail.ffid.trim() : '';

    const requestMetadata: RequestMetadata = {
      requestType: dataRequestType ?? '',
      requestHeaderDisplayName:
        typeof requestDetail.requestHeaderDisplayName === 'string'
          ? requestDetail.requestHeaderDisplayName.trim()
          : '',
      ffRoleName,
      requestId: requestDetail.requestId,
      requestAccessKey: requestDetail.requestAccessKey,
      taskId: requestDetail.taskId ?? 0,
      connectiontype: requestDetail.connectionType ?? null,
      applicationType: requestDetail.applicationType ?? null,
      requestorUserDisplayName: requestDetail.requestorUserDisplayName ?? '',
      ffid,
      ticketNumber:
        requestDetail.ticketNumber != null
          ? String(requestDetail.ticketNumber).trim()
          : '',
      businessJustification: requestDetail.businessJustification ?? '',
      reasonCode: requestDetail.reasonCode ?? '',
      requestStartedAt: requestDetail.requestStartedAt ?? '',
      requestEndedAt:
        requestDetail.requestEndedAt != null
          ? String(requestDetail.requestEndedAt)
          : '',
      sourceTimeZone:
        requestDetail.sourceTimeZone != null
          ? String(requestDetail.sourceTimeZone)
          : '',
      requestedEntitlementDisplayNames:
        requestDetail.requestedEntitlementDisplayNames ?? [],
      endpointKey: requestDetail.endpointKey ?? 0,
      endpointDisplayName: requestDetail.endpointDisplayName?.trim() ?? '',
      endpointName: requestDetail.endpointName?.trim() ?? '',
      endpointLogo: requestDetail.endpointLogo?.trim() ?? '',
      approverUserDisplayName: requestDetail.approverUserDisplayName ?? null,
      readOnly: Boolean(readOnly),
    };

    const launchDetail = Array.isArray(
      (requestDetail as { launchDetail?: unknown }).launchDetail
    )
      ? (
          (
            requestDetail as {
              launchDetail?: {
                launchId: number;
                launchStartDate: string;
                launchEndDate: string;
              }[];
            }
          ).launchDetail ?? []
        ).map((d) => ({
          launchId: Number(d.launchId),
          launchStartDate: String(d.launchStartDate),
          launchEndDate: String(d.launchEndDate),
        }))
      : [];

    return {
      requestMetadata,
      isReadOnly: Boolean(readOnly),
      reviewSummary: reviewDetail,
      state,
      launchDetail,
      isActivityLogEnabled,
      exportMaxDateRangeMonths,
    };
  }

  throw new Error('Failed to load request review details');
};

export const auditLogApi = createApi({
  reducerPath: AuditLogType.API_SLICE_NAME,
  baseQuery: axiosBaseQuery({}),
  tagTypes: ['AuditLogs', 'AuditReviews', 'AuditFilters', 'Insights'],
  keepUnusedDataFor: 300, // 5 minutes cache
  endpoints: () => ({}),
});

// Extend API with additional endpoints
export const auditLogApiEndpoints = auditLogApi.injectEndpoints({
  endpoints: (builder) => ({
    // Create async export for audit logs
    createAuditLogExport: builder.mutation<
      unknown,
      {
        locale: string;
        connectionType?: string;
        filters: Record<string, unknown>;
      }
    >({
      query: (payload) => ({
        url: `${ACTIVITY_LOG_INTL_BASE_URL}/logs/export/async`,
        method: 'POST',
        data: payload,
        isECMv6: true,
        config: {},
      }),
    }),
    // Get insights list for a request
    getInsights: builder.query<
      {
        items: {
          id: string;
          description: string;
          severity?: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
          entitlements?: {
            entitlementDisplayName: string;
            entitlementDescription?: string | null;
            entitlementUsage?: number | string;
            entitlementDeviation?: boolean;
          }[];
        }[];
        riskScore?: number;
        riskSeverity?: string;
      },
      { requestAccessKey: number | string }
    >({
      query: ({ requestAccessKey }) => ({
        url: `${ACTIVITY_LOG_INTL_BASE_URL}/insights`,
        method: 'GET',
        params: { requestAccessKey },
        isECMv6: true,
      }),
      transformResponse: (response: unknown) => {
        const obj = response as InsightsApiResponse | null | undefined;

        if (
          obj &&
          typeof obj === 'object' &&
          obj.status === 200 &&
          typeof obj.data === 'object'
        ) {
          const toInsightSeverity = (value: unknown) => {
            const severityRaw = String(value ?? '').toUpperCase();
            const allowed = [
              'CRITICAL',
              'HIGH',
              'MEDIUM',
              'LOW',
              'NONE',
              'NORISK',
              'LOW_RISK',
            ] as const;

            return allowed.includes(severityRaw as (typeof allowed)[number])
              ? (severityRaw as (typeof allowed)[number])
              : undefined;
          };

          const riskRaw = (
            obj.data as {
              risk?: { riskScore?: unknown; riskSeverity?: unknown };
            }
          ).risk;

          const riskScoreNum = (() => {
            const v = riskRaw?.riskScore;
            const n =
              typeof v === 'string' || typeof v === 'number' ? Number(v) : NaN;

            if (Number.isFinite(n)) {
              return Math.max(0, Math.min(100, n));
            }

            return undefined;
          })();

          const riskSeverity = (() => {
            const v = riskRaw?.riskSeverity;

            return typeof v === 'string' ? v : undefined;
          })();

          const itemsUnknown = Array.isArray(obj.data.results)
            ? (obj.data.results as unknown[])
            : [];

          const items = itemsUnknown
            .map((it) => {
              const rec = it as {
                insightId?: unknown;
                description?: unknown;
                params?: unknown;
                severity?: unknown;
                entitlements?: unknown;
              };

              const id = String(rec.insightId ?? '');
              const description = String(rec.description ?? '');
              const params =
                rec.params !== null &&
                typeof rec.params === 'object' &&
                !Array.isArray(rec.params)
                  ? (rec.params as Record<string, string | number>)
                  : undefined;
              const severity = toInsightSeverity(rec.severity);

              const entitlements = Array.isArray(rec.entitlements)
                ? (rec.entitlements as unknown[])
                    .map((e) => {
                      const er = e as {
                        entitlementDisplayName?: unknown;
                        entitlementDescription?: unknown;
                        entitlementUsage?: unknown;
                        entitlementDeviation?: unknown;
                      };

                      const entitlementDisplayName = String(
                        er.entitlementDisplayName ?? ''
                      );

                      if (!entitlementDisplayName) {
                        return null;
                      }

                      return {
                        entitlementDisplayName,
                        entitlementDescription:
                          (er.entitlementDescription as string | null) ?? null,
                        entitlementUsage:
                          (er.entitlementUsage as
                            | number
                            | string
                            | undefined) ?? undefined,
                        entitlementDeviation: Boolean(er.entitlementDeviation),
                      };
                    })
                    .filter(Boolean)
                : undefined;

              if (!id || !description) {
                return null;
              }

              return { id, description, params, severity, entitlements };
            })
            .filter(Boolean) as {
            id: string;
            description: string;
            params?: Record<string, string | number>;
            severity?: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
            entitlements?: {
              entitlementDisplayName: string;
              entitlementDescription?: string | null;
              entitlementUsage?: number | string;
              entitlementDeviation?: boolean;
            }[];
          }[];

          return { items, riskScore: riskScoreNum, riskSeverity };
        }

        return { items: [] };
      },
      providesTags: ['Insights'],
    }),
    // Get activity summary counts for a request
    getAuditSummary: builder.query<
      { flagged: number; commented: number; resolved: number },
      { requestAccessKey: number | string }
    >({
      query: ({ requestAccessKey }) => ({
        url: `${ACTIVITY_LOG_INTL_BASE_URL}/logs/summary`,
        method: 'GET',
        params: { requestAccessKey },
        isECMv6: true,
      }),
      transformResponse: (response: unknown) => {
        const obj = response as {
          status?: number;
          data?: { flagged?: unknown; commented?: unknown; resolved?: unknown };
        };

        if (obj.status === 200 && obj.data && typeof obj.data === 'object') {
          const { flagged, commented, resolved } = obj.data;

          return {
            flagged: Number(flagged ?? 0),
            commented: Number(commented ?? 0),
            resolved: Number(resolved ?? 0),
          };
        }

        return { flagged: 0, commented: 0, resolved: 0 };
      },
      providesTags: ['AuditLogs'],
    }),
    // Get request review details
    getRequestReviewDetails: builder.query<
      {
        requestMetadata: RequestMetadata | null;
        isReadOnly: boolean;
        reviewSummary: ReviewDetailInfo | null;
        state: ReviewState;
        launchDetail: {
          launchId: number;
          launchStartDate: string;
          launchEndDate: string;
        }[];
        isActivityLogEnabled?: boolean;
        exportMaxDateRangeMonths: number;
      },
      { requestAccessKey: number }
    >({
      query: ({ requestAccessKey }) => ({
        url: `${ACTIVITY_LOG_INTL_BASE_URL}/requests/${requestAccessKey}/requestReviewDetails`,
        method: 'GET',
        isECMv6: true,
      }),
      transformResponse: (response: unknown) =>
        transformRequestReviewDetailsApiResponse(
          response as RequestReviewDetailsApiResponse
        ),
    }),

    // Fetch Functions by endpoint key
    getFunctions: builder.query<
      { functionId: number; functionName: string }[],
      {
        endpointKey: string | number;
        functionName?: string;
        connectionType?: string;
        requestAccessKey: number | string;
      }
    >({
      query: ({
        endpointKey,
        functionName,
        connectionType,
        requestAccessKey,
      }) => ({
        url: `${ACTIVITY_LOG_INTL_BASE_URL}/functions`,
        method: 'GET',
        params:
          functionName && functionName.length > 0
            ? { endpointKey, functionName, connectionType, requestAccessKey }
            : { endpointKey, connectionType, requestAccessKey },
        config: {},
        isECMv6: true,
      }),
      transformResponse: (response: unknown) => {
        const obj = response as {
          status?: number;
          message?: string;
          data?: unknown;
        };

        if (obj.status === 200 && Array.isArray(obj.data)) {
          const rawData: FunctionRec[] = obj.data as FunctionRec[];

          return mapUniqueFunctions(rawData);
        }

        return [];
      },
      providesTags: ['AuditFilters'],
    }),
    // Search entitlements for Transaction Overview list (mocked)
    searchEntitlements: builder.mutation<
      EntitlementsSearchResponse,
      SearchEntitlementsRequest
    >({
      query: (payload) => ({
        url: `${ACTIVITY_LOG_INTL_BASE_URL}/logs/entitlements/search`,
        method: 'POST',
        data: payload,
        isECMv6: true,
        config: {},
      }),
      transformResponse: (response: unknown): EntitlementsSearchResponse => {
        if (!response || typeof response !== 'object') {
          return { afterKey: null, data: [] };
        }

        const obj = response as {
          status?: number;
          data?: {
            afterKey?: unknown;
            results?: unknown[];
          };
        };

        if (obj.status !== 200 || !obj.data) {
          return { afterKey: null, data: [] };
        }

        const afterKeyRaw = obj.data.afterKey;
        const afterKey =
          typeof afterKeyRaw === 'number' || typeof afterKeyRaw === 'string'
            ? afterKeyRaw
            : null;

        const itemsUnknown = Array.isArray(obj.data.results)
          ? obj.data.results
          : [];
        const normalized = itemsUnknown.map((r) => {
          const rec = r as Record<string, unknown>;

          return {
            entitlementValueKey: rec.entitlementValueKey as number | string,
            entitlementValue: String(rec.entitlementValue ?? ''),
            entitlementDisplayName: (rec.entitlementDisplayName ?? null) as
              | string
              | null,
            entitlementDescription: (rec.entitlementDescription ?? null) as
              | string
              | null,
            entitlementSoxSeverity: (rec.entitlementSoxSeverity ?? '0') as
              | string
              | number,
            entitlementSysSeverity: (rec.entitlementSysSeverity ?? '0') as
              | string
              | number,
            functionNames: Array.isArray(rec.functionNames)
              ? (rec.functionNames as unknown[]).map((v) => String(v))
              : null,
            businessProcessNames: Array.isArray(rec.businessProcessNames)
              ? (rec.businessProcessNames as unknown[]).map((v) => String(v))
              : null,
            entitlementDeviation: (rec.entitlementDeviation ?? false) as
              | boolean
              | string,
            entitlementUsageCount: Number(rec.entitlementUsageCount ?? 0),
            entitlementUsageDuration: (rec.entitlementUsageDuration ?? 0) as
              | number
              | string,
          };
        });

        return { afterKey, data: normalized };
      },
    }),
    // Search audit logs
    searchAuditLogs: builder.mutation<
      AuditLogSearchResponse,
      AuditLogSearchRequest
    >({
      query: (payload) => ({
        url: `${ACTIVITY_LOG_INTL_BASE_URL}/logs/search`,
        method: 'POST',
        data: payload,
        config: {},
        isECMv6: true,
      }),
      transformResponse: (
        response: AuditLogSearchApiResponse
      ): AuditLogSearchResponse => {
        if (response.status === 200) {
          const {
            data: { results, searchAfter, totalRecords },
          } = response;

          const safeResults: TimelineData[] = Array.isArray(results)
            ? results
            : [];

          const safeTotalRecords: number =
            typeof totalRecords === 'number'
              ? totalRecords
              : safeResults.length;

          const transformed: AuditLogSearchResponse = {
            data: safeResults,
            totalRecords: safeTotalRecords,
            searchAfter: searchAfter ?? null,
          };

          return transformed;
        }

        // Surface error to caller; UI will show error screen
        throw new Error('Failed to search audit logs');
      },
    }),

    // Complete review
    completeReview: builder.mutation<
      CompleteReviewResponse,
      CompleteReviewRequest
    >({
      query: ({ requestAccessKey, body }) => ({
        url: `${ACTIVITY_LOG_INTL_BASE_URL}/requests/${requestAccessKey}/complete`,
        method: 'POST',
        data: body,
        isECMv6: true,
      }),
      transformResponse: (
        response: CompleteReviewApiResponse
      ): CompleteReviewResponse => {
        if (response.status === 200) {
          return {
            success: true,
            message: response.message || 'Review completed successfully',
          };
        }

        throw new Error(response.message || 'Failed to Complete Review');
      },
    }),

    // Fetch entitlements for Entitlement pill (searchable)
    getEntitlements: builder.mutation<
      string[],
      {
        endpoint: string;
        searchString: string;
        max?: number;
        offset?: number;
      }
    >({
      query: ({ endpoint, searchString, max = 100, offset = 0 }) => ({
        url: '/api/v5/getEntitlements',
        method: 'POST',
        params: { max, offset },
        data: {
          endpoint,
          exactmatch: 'FALSE',
          entitlementfiltercriteria: {
            entitlement_value: searchString,
          },
        },
      }),
      transformResponse: (response: unknown): string[] => {
        if (!response) {
          return [];
        }

        const obj = response as Record<string, unknown>;

        const errorCode =
          typeof obj.errorCode === 'string' ? obj.errorCode : undefined;

        const status = typeof obj.status === 'number' ? obj.status : undefined;

        const rawDetails = (obj as { Entitlementdetails?: unknown })
          .Entitlementdetails;
        const details: unknown[] = Array.isArray(rawDetails) ? rawDetails : [];

        const isOk = errorCode === '0' || status === 200;

        if (!isOk || details.length === 0) {
          return [];
        }

        return Array.from(
          new Set(
            details
              .map((item) => {
                if (item && typeof item === 'object') {
                  const v = (item as { entitlement_value?: unknown })
                    .entitlement_value;

                  return typeof v === 'string' ? v : '';
                }

                return '';
              })
              .filter((v) => v.length > 0)
          )
        );
      },
    }),

    // Fetch audit columns by view
    getAuditColumns: builder.query<
      {
        filters: { id: string; localizationKey: string; isDefault: boolean }[];
        entitlementFilters?: AuditFilterDef[];
      } & Record<string, AuditColumnDefApiResponse[]>,
      { view: string; requestAccessKey: string | number }
    >({
      query: ({ view, requestAccessKey }) => ({
        url: `${ACTIVITY_LOG_INTL_BASE_URL}/view/columns`,
        method: 'GET',
        params: { view, requestAccessKey },
        config: {},
        isECMv6: true,
      }),
      transformResponse: (response: unknown) => {
        const obj = response as GetAuditColumnsApiResponse;
        const { status, data } = obj;

        if (status === 200 && data && typeof data === 'object') {
          const {
            auditLogFilters = [],
            entitlementFilters = [],
            securityAuditLog = [],
            changeAuditLog = [],
            tcodeAuditLog = [],
            systemAuditLog = [],
          } = data as unknown as {
            requestHeader?: AuditFilterDef[];
            requestDetail?: AuditFilterDef[];
            reviewDetail?: AuditFilterDef[];
            auditLogFilters?: {
              id: string;
              localizationKey: string;
              isDefault: boolean;
            }[];
            entitlementFilters?: AuditFilterDef[];
            securityAuditLog?: AuditColumnDefApiResponse[];
            changeAuditLog?: AuditColumnDefApiResponse[];
            tcodeAuditLog?: AuditColumnDefApiResponse[];
            systemAuditLog?: AuditColumnDefApiResponse[];
          };

          return {
            filters: auditLogFilters,
            entitlementFilters,
            securityAuditLog,
            changeAuditLog,
            tcodeAuditLog,
            systemAuditLog,
          } as {
            filters: {
              id: string;
              localizationKey: string;
              isDefault: boolean;
            }[];
            entitlementFilters?: AuditFilterDef[];
          } & Record<string, AuditColumnDefApiResponse[]>;
        }

        return {
          filters: [],
          entitlementFilters: [],
        } as {
          filters: {
            id: string;
            localizationKey: string;
            isDefault: boolean;
          }[];
          entitlementFilters?: AuditFilterDef[];
        } & Record<string, AuditColumnDefApiResponse[]>;
      },
    }),

    // Get review activities for a log item
    getAuditLogReviews: builder.query<
      { reviewActivities: ReviewActivity[] },
      { uniqueLogId: string | number; requestAccessKey: number | string }
    >({
      query: ({ uniqueLogId, requestAccessKey }) => ({
        url: `${ACTIVITY_LOG_INTL_BASE_URL}/logs/${uniqueLogId}/reviews`,
        method: 'GET',
        params: { requestAccessKey },
        config: {},
        isECMv6: true,
      }),
      transformResponse: (response: PostReviewApiResponse | unknown) => {
        const obj = response as PostReviewApiResponse;
        const raw = obj.data?.reviewActivities;

        const normalize = (items: unknown): ReviewActivity[] => {
          if (!Array.isArray(items)) {
            return [];
          }

          return items.map((r) => {
            const rec = r as Partial<ReviewActivity> & { status?: string };
            const rawStatus = typeof rec.status === 'string' ? rec.status : '';
            const normalized = rawStatus.trim().toUpperCase();

            let status: ReviewActivity['status'] = 'COMMENTED';

            if (
              normalized.includes('FLAG') &&
              normalized.includes('RESOLVED')
            ) {
              status = 'RESOLVED';
            } else if (normalized.includes('FLAG')) {
              status = 'FLAGGED';
            } else if (normalized === 'RESOLVED') {
              status = 'RESOLVED';
            }

            return {
              status,
              user: typeof rec.user === 'string' ? rec.user : 'John',
              comment: typeof rec.comment === 'string' ? rec.comment : '',
              updatedAt:
                typeof rec.updatedAt === 'string' ||
                typeof rec.updatedAt === 'number'
                  ? rec.updatedAt
                  : Date.now(),
            };
          });
        };

        const parsed = normalize(raw);

        if (parsed.length > 0) {
          return { reviewActivities: parsed };
        }

        return { reviewActivities: [] }; // Return empty array instead of mockActivities
      },
      providesTags: (result, error, arg) => [
        { type: 'AuditReviews', id: String(arg.uniqueLogId) },
      ],
    }),

    // Post review action on a log item
    postAuditLogReview: builder.mutation<
      { reviewActivities: ReviewActivity[] },
      PostReviewRequest & { requestAccessKey: number | string }
    >({
      query: ({ uniqueLogId, body, requestAccessKey }) => ({
        url: `${ACTIVITY_LOG_INTL_BASE_URL}/logs/${uniqueLogId}/review`,
        method: 'POST',
        params: { requestAccessKey },
        data: body,
        config: {},
        isECMv6: true,
      }),
      transformResponse: (response: PostReviewApiResponse | unknown) => {
        const obj = response as PostReviewApiResponse;
        const raw = obj.data?.reviewActivities;

        const activities: ReviewActivity[] = Array.isArray(raw)
          ? raw.map((r) => {
              let status: ReviewActivity['status'] = 'COMMENTED';

              if (r.status === 'FLAGGED') {
                status = 'FLAGGED';
              } else if (r.status === 'RESOLVED') {
                status = 'RESOLVED';
              }

              return {
                status,
                user: typeof r.user === 'string' ? r.user : '',
                comment: typeof r.comment === 'string' ? r.comment : '',
                updatedAt:
                  typeof r.updatedAt === 'string' ||
                  typeof r.updatedAt === 'number'
                    ? r.updatedAt
                    : '',
              };
            })
          : [];

        return { reviewActivities: activities };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'AuditReviews', id: String(arg.uniqueLogId) },
        // If you want to refresh the main logs list after a review, also include 'AuditLogs'
        // 'AuditLogs',
      ],
    }),

    // Fetch filter options for filters pills
    getAuditFiltersOptions: builder.query<
      Record<string, { options: { label: string; value: unknown }[] }>,
      {
        requestAccessKey: string | number;
        requestType: 'activitylog' | 'entitlement';
      }
    >({
      query: ({ requestAccessKey, requestType }) => ({
        url: `${ACTIVITY_LOG_INTL_BASE_URL}/logs/filters`,
        method: 'GET',
        params: { requestAccessKey, requestType },
        config: {},
        isECMv6: true,
      }),
      transformResponse: (response: unknown) => {
        const obj = response as AuditFiltersOptionsApiResponse;
        const { status, data } = obj;

        if (status === 200 && data && typeof data === 'object') {
          const normalized: Record<
            string,
            { options: { label: string; value: unknown }[] }
          > = {};

          Object.keys(data).forEach((key) => {
            const entry = data[key];
            const rawOptions = Array.isArray(entry.options)
              ? entry.options
              : [];

            // Title-case labels for changeIndicators specifically
            const opts = rawOptions.map((o) => {
              if (key === 'changeIndicators' && typeof o.label === 'string') {
                const lower = o.label.toLowerCase();
                const titled = lower.charAt(0).toUpperCase() + lower.slice(1);

                return { ...o, label: titled };
              }

              return o;
            });

            normalized[key] = { options: opts };
          });

          return normalized;
        }

        return {} as Record<
          string,
          { options: { label: string; value: unknown }[] }
        >;
      },
      providesTags: ['AuditFilters'],
    }),
  }),
  overrideExisting: false,
});

// Export hooks for use in components
export const {
  useGetInsightsQuery,
  useLazyGetInsightsQuery,
  useGetAuditSummaryQuery,
  useLazyGetAuditSummaryQuery,
  useCreateAuditLogExportMutation,
  useSearchAuditLogsMutation,
  useGetEntitlementsMutation,
  useGetAuditColumnsQuery,
  usePostAuditLogReviewMutation,
  useGetAuditLogReviewsQuery,
  useLazyGetAuditLogReviewsQuery,
  useGetAuditFiltersOptionsQuery,
  useGetFunctionsQuery,
  useLazyGetFunctionsQuery,
  useGetRequestReviewDetailsQuery,
  useLazyGetRequestReviewDetailsQuery,
  useSearchEntitlementsMutation,
  useCompleteReviewMutation,
} = auditLogApiEndpoints;
